import { ReactNode, useState, useMemo, useEffect } from 'react';

import { Market } from '#/api/markets';

import { useTradeActiveMarketStoreView } from '#/features/perpetuals/active-market-context';
import { useActiveMarketSummaryState } from '#/features/perpetuals/market-info/markets-summary-context';
import { useWalletView } from '#/features/wallet/wallet-context';
import { useAccountView } from '#/features/account/account-context';

import createSafeContext from '#/utils/createSafeContext';
import usePrevious from '#/utils/usePrevious';

import { OrderFormView, createView } from './form-view';
import { INITIAL_STATE } from './form-state';
import { OrderFormActions, createActions } from './form-actions';

export const [useOrderFormView, ViewCtx] =
  createSafeContext<OrderFormView>('OrderFormView');
export const [useOrderFormActions, ActionsCtx] =
  createSafeContext<OrderFormActions>('OrderFormActions');

interface ProviderProps {
  readonly children: ReactNode;
}

export function OrderFormProvider({ children }: ProviderProps) {
  const { activeMarket } = useTradeActiveMarketStoreView();
  const activeMarketSummary = useActiveMarketSummaryState();
  const walletView = useWalletView();

  const [state, setState] = useCustomState(activeMarket);

  const accountView = useAccountView();

  const actions = useMemo(
    () => createActions(setState, activeMarket, activeMarketSummary),
    [activeMarket, activeMarketSummary, setState],
  );
  const view = useMemo(
    () =>
      createView(
        state,
        activeMarket,
        activeMarketSummary,
        walletView.isSignedIn,
        accountView.isBeingLiquidated,
      ),
    [
      state,
      activeMarket,
      activeMarketSummary,
      walletView.isSignedIn,
      accountView.isBeingLiquidated,
    ],
  );

  return (
    <ActionsCtx value={actions}>
      <ViewCtx value={view}>{children}</ViewCtx>
    </ActionsCtx>
  );
}

/**
 * Custom useState hook to handle the state of the
 * order form when the active market changes.
 */
function useCustomState(activeMarket: Market | null) {
  const [state, setState] = useState(INITIAL_STATE);
  const prevActiveMarket = usePrevious(activeMarket);

  useEffect(() => {
    if (activeMarket == null) return;
    setState(initializeState(activeMarket));
  }, [activeMarket, setState]);

  if (activeMarket == null) {
    return [state, setState] as const;
  }

  const hasMarketChanged = activeMarket.symbol === prevActiveMarket?.symbol;
  if (hasMarketChanged) {
    return [state, setState] as const;
  }

  // Return the initial state immediately
  // while the new state is being calculated
  return [initializeState(activeMarket), setState] as const;
}

/**
 * Calculate the initial state based on the active market
 */
function initializeState(market: Market) {
  return {
    ...INITIAL_STATE,
    asset: market.base_currency,
  };
}
